import Chess from 'chess.js';
import pgnParser from '../../generated/pgn-parser.js';
import { parseFenFromUrl } from '../../utils.js';

// Browser ESM and Node's CommonJS entry expose chess.js differently.
const ChessConstructor = typeof Chess === 'function' ? Chess : Chess.Chess;

export const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

// Complete omitted fields without checking chess legality or changing pieces.
export function normalizeFen(input) {
  const fen = parseFenFromUrl(input);
  if (!fen) return '';
  const parts = fen.split(' ');
  const defaults = ['w', '-', '-', '0', '1'];
  while (parts.length < 6) parts.push(defaults[parts.length - 1]);
  return parts.join(' ');
}

// The displayed FEN and engine request do not depend on chess.js accepting it.
export function createGame(fen) {
  const displayOnly = {
    displayOnly: true,
    SQUARES: [],
    fen: () => fen,
    header: () => ({ FEN: fen, SetUp: '1' }),
    history: () => [],
    moves: () => [],
    move: () => null,
    turn: () => fen.split(' ')[1] === 'b' ? 'b' : 'w',
    pgn: () => '',
    in_check: () => false,
    in_checkmate: () => false,
    in_stalemate: () => false,
    in_draw: () => false,
    game_over: () => false,
  };

  try {
    const game = new ChessConstructor();
    game.clear();
    if (game.load(fen) === false) return displayOnly;

    // A loader must not silently discard pieces from the displayed position.
    if (game.fen().split(' ')[0] !== fen.split(' ')[0]) return displayOnly;

    // Local move/notation failures must not prevent displaying or analyzing.
    for (const [name, fallback] of Object.entries(displayOnly)) {
      if (typeof fallback !== 'function') continue;
      const original = game[name].bind(game);
      game[name] = (...args) => {
        try { return original(...args); }
        catch { return fallback(...args); }
      };
    }
    return game;
  } catch {
    return displayOnly;
  }
}

export function rootFen(game) { return game.header().FEN || START_FEN; }

export function cloneGame(game, moveNumber = -1) {
  if (game.displayOnly) return game;
  const clone = createGame(rootFen(game));
  if (clone.displayOnly) return clone;
  const headers = Object.entries(game.header()).flat();
  if (headers.length) clone.header(...headers);
  const moves = game.history({ verbose: true });
  for (let i = 0; i <= moveNumber && i < moves.length; i++) {
    if (!clone.move(moves[i])) break;
  }
  return clone;
}

// Include reversible move history so Stockfish can recognize repetitions.
export function enginePosition(game, lastMoveId, displayedFen = game.fen()) {
  if (game.displayOnly || lastMoveId < 0) return `position fen ${displayedFen}`;
  const moves = game.history({ verbose: true }).slice(0, lastMoveId + 1)
    .map(move => move.from + move.to + (move.promotion || ''));
  return `position fen ${rootFen(game)}${moves.length ? ` moves ${moves.join(' ')}` : ''}`;
}

export const fixPgnInput = value => {
  value = value.trim();
  return /(?:1-0|0-1|1\/2-1\/2|\*)$/.test(value) ? value : value + ' *';
};

export function parsePgn(value) {
  if (!value || !value.trim()) return null;
  try {
    const parsed = pgnParser.parse(fixPgnInput(value));
    if (parsed.length !== 1) return null;
    const headers = parsed[0].headers || [];
    const fen = normalizeFen(headers.find(h => h.name === 'FEN')?.value ?? START_FEN);
    const game = createGame(fen);
    if (game.displayOnly) return null;
    for (const h of headers) if (!['FEN', 'SetUp'].includes(h.name)) game.header(h.name, h.value);
    for (const { move } of parsed[0].moves) if (!game.move(move)) return null;
    return game;
  } catch (_) { return null; }
}

export function getPgn(game) {
  if (game.displayOnly) return '';
  let pgn = game.pgn().replace(/\. \.\.\./g, '...').trim();
  if (!/(?:1-0|0-1|1\/2-1\/2|\*)$/.test(pgn)) pgn += (pgn ? ' ' : '') + '*';
  return pgn;
}

export function getEvalText({ score, mate }) {
  if (mate) return score === 0 ? '#0' : `#${score}`;
  return `${score >= 0 ? '+' : ''}${(score / 100).toFixed(1)}`;
}

// Used only to label a no-moves result; never blocks sending a position.
export function terminalLabel(game) {
  if (!game || game.displayOnly) return '';
  try {
    const parts = game.fen().split(' ');
    const placement = parts[0];
    const pieces = placement.replace(/[1-8/]/g, '');
    const ranks = placement.split('/');
    if ((pieces.match(/K/g) || []).length !== 1 ||
        (pieces.match(/k/g) || []).length !== 1 ||
        (pieces.match(/P/g) || []).length > 8 ||
        (pieces.match(/p/g) || []).length > 8 ||
        (pieces.match(/[A-Z]/g) || []).length > 16 ||
        (pieces.match(/[a-z]/g) || []).length > 16 ||
        /[pP]/.test(ranks[0] + ranks[7])) return '';
    const opposite = [...parts];
    opposite[1] = parts[1] === 'w' ? 'b' : 'w';
    opposite[3] = '-';
    const previousSide = createGame(opposite.join(' '));
    if (previousSide.displayOnly || previousSide.in_check()) return '';
    if (game.in_checkmate()) return 'Checkmate';
    if (game.in_stalemate()) return 'Stalemate';
  } catch { /* An unsupported position keeps the generic engine message. */ }
  return '';
}

export function engineStatusText(state, game) {
  if (!state.engineOn) return 'engine';
  if (state.engineStatus === 'error') return state.engineError || 'Engine unavailable';
  if (state.engineStatus === 'no-moves') return terminalLabel(game) || 'Engine cannot analyze this position';
  if (state.engineStatus === 'loading') return state.engineKind === 'js' ? 'Loading Stockfish…' : 'Loading Stockfish NNUE…';
  if (state.engineStatus === 'paused') return 'Paused';
  const first = state.evals[0];
  return first ? `Depth ${first.depth}${state.engineStatus === 'complete' ? ' · done' : ''}` : 'Analyzing…';
}

export function makeEngineLines(evals, fen, count, maxLength) {
  return Array.from({ length: count }, (_, index) => {
    const game = createGame(fen);
    let useSan = !game.displayOnly;
    return (evals[index]?.pv || []).slice(0, maxLength).map(move => {
      if (useSan) {
        const played = game.move(move);
        if (played) return played.san;
        useSan = false;
      }
      return move.from + move.to + (move.promotion || '');
    });
  });
}

export function readerLayout(width, height, engineOn) {
  // Preserve legacy formulas at normal embed sizes; clamp collapsed WebViews.
  const landscape = Math.max(112, Math.min(height - 50, width - 200));
  const portrait = Math.max(1, Math.min(width, height - (engineOn ? 145 : 100)));
  const isLandscape = width >= 312 && landscape > portrait;
  return { isLandscape, boardSize: isLandscape ? landscape : portrait };
}
